import React, { useMemo } from 'react';
import { TrackDownload } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowUpRight, Music, Download } from 'lucide-react';
import { ChartContainer, BarChart, XAxis, YAxis, CartesianGrid, ChartTooltip, ChartTooltipContent, Bar } from './CustomChart';
import { useIsMobile } from '@/hooks/use-mobile';
import { format } from 'date-fns';

interface TopTracksTableProps {
  uploads: TrackDownload[];
}

const TopTracksTable: React.FC<TopTracksTableProps> = ({ uploads }) => {
  const isMobile = useIsMobile();
  
  const topTracks = useMemo(() => {
    // Get unique tracks based on trackName
    const uniqueTracks = uploads.reduce((acc, current) => {
      const existing = acc.find(item => item.trackName === current.trackName);
      
      if (existing) {
        // If this track already exists in our list, add its downloads to the existing entry
        existing.downloadCount += current.downloadCount;
      } else {
        // Otherwise add this as a new track
        acc.push({...current});
      }
      
      return acc;
    }, [] as TrackDownload[]);
    
    // Sort tracks by total download count (descending)
    return [...uniqueTracks]
      .sort((a, b) => b.downloadCount - a.downloadCount)
      .slice(0, 5); // Get top 5 tracks
  }, [uploads]);

  const chartData = useMemo(() => {
    return topTracks.map(track => ({
      name: track.trackName || track.fileName.split('.')[0] || 'Unnamed Track',
      downloads: track.downloadCount,
      date: format(new Date(track.createdAt), 'MMM dd, yyyy')
    }));
  }, [topTracks]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="w-full">
      <Card className="w-full overflow-hidden border-border shadow-md">
        <CardHeader>
          <CardTitle>Top Performing Tracks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ChartContainer
              className="h-[300px] w-full"
              config={{
                downloads: {
                  label: "Downloads",
                  color: "#8884d8"
                }
              }}
            >
              <BarChart 
                data={chartData}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={!isMobile} horizontal={isMobile} stroke="#444" />
                {isMobile ? (
                  <>
                    <XAxis type="number" stroke="#888" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }} 
                      width={100}
                      stroke="#888"
                    />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 12 }}
                      stroke="#888"
                    />
                    <YAxis allowDecimals={false} stroke="#888" />
                  </>
                )}
                <ChartTooltip
                  content={<ChartTooltipContent />}
                />
                <Bar 
                  dataKey="downloads" 
                  fill="#8884d8" 
                  radius={[4, 4, 0, 0]}
                  background={{ fill: '#444' }}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="w-full mt-6 overflow-hidden border-border shadow-md">
        <CardHeader>
          <CardTitle>Track Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">#</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Track</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Release Date</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Downloads</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {topTracks.map((track, index) => (
                  <tr key={track.id} className="border-b border-border/50 hover:bg-muted/40 transition-colors">
                    <td className="py-3 px-4">{index + 1}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-1.5 rounded-md mr-3">
                          <Music className="h-4 w-4 text-primary" />
                        </div>
                        <div className="font-medium truncate max-w-[180px]">
                          {track.trackName || track.fileName.split('.')[0] || 'Unnamed Track'}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {formatDate(track.createdAt)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Download className="h-4 w-4 text-green-500" />
                        <span className="font-medium tabular-nums">{track.downloadCount}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <a
                        href={`/download?id=${track.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        View <ArrowUpRight className="ml-1 h-3 w-3" />
                      </a>
                    </td>
                  </tr>
                ))}
                
                {topTracks.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      No track data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TopTracksTable;
